import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Group,
    Text,
} from '@mantine/core';
import {TableFormattedTimestamp} from '@flexinet/ui-components';

import {
    isWritableDeclared,
    ProviderConnection,
} from '../types';
import {ConnectionStatusBadge} from '../components/ConnectionStatusBadge';
import config from '../config';

// FNP-1248 — the index table's columns.
//
// EVERY column is sortable (the module checklist is explicit), through the
// shared comparator `sortRecords` applied in useProviderConnections.

/**
 * A one-line summary of what this credential can do.
 *
 * ⚠️ SAYS "READ-ONLY", NOT "NO WRITE ACCESS" OR A RED CROSS. A tenant who
 * deliberately supplied a read-only role has a correct, working connection —
 * the summary states what it IS, not what it lacks. Framing absence as a
 * deficiency in a list view is how a careful setup starts looking broken.
 */
const capabilitySummary = (connection: ProviderConnection): string => {
    const capabilities = connection.capabilities;

    if (!capabilities) {
        return I18n.get('Not recorded');
    }

    const writes: string[] = [];
    if (capabilities.canCreateZones) {
        writes.push(I18n.get('create zones'));
    }
    if (capabilities.canDeleteZones) {
        writes.push(I18n.get('delete zones'));
    }
    if (capabilities.canWriteRecords) {
        writes.push(I18n.get('write records'));
    }
    if (capabilities.canSetNameservers) {
        writes.push(I18n.get('set nameservers'));
    }

    if (writes.length === 0) {
        return I18n.get('Read-only');
    }

    return writes.join(', ');
};

export const useProviderConnectionsTableColumns = () => {

    const columns: DataTableColumn<ProviderConnection>[] = [
        {
            accessor: 'displayName',
            // ⚠️ THE FLEXIBLE COLUMN CLAIMS THE SLACK (FNP-1284). Matches
            // `certificates`, the module the owner verified in a browser as
            // showing no scrollbar on an empty table. Without a fraction here
            // nothing absorbs the remaining space, and with no rows to
            // constrain the layout the fixed columns beside it can drive the
            // table past its container — which is exactly what happened to
            // zones. Enforced by tests/tableColumnWidths.
            width: '2fr',
            sortable: true,
            title: I18n.get('Name'),
            render: (item: ProviderConnection) => (
                <NavLink to={`${config.basePath}/${encodeURIComponent(item.id)}`}>
                    <Text size={'sm'}>{item.displayName}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'providerId',
            sortable: true,
            title: I18n.get('Provider'),
            width: '150px',
            render: (item: ProviderConnection) => (
                <Badge variant={'light'} color={'blue'}>{item.providerId}</Badge>
            ),
        },
        {
            accessor: 'status',
            sortable: true,
            title: I18n.get('Status'),
            width: '190px',
            // ⚠️ Sorts on the raw `status` enum but RENDERS the four-state
            // presentation. The sort key stays the schema's field so the
            // ordering is stable and explicable; the badge tells the truth
            // about which of the two meanings of INVALID this row is.
            render: (item: ProviderConnection) => <ConnectionStatusBadge connection={item}/>,
        },
        {
            accessor: 'capabilities',
            sortable: true,
            title: I18n.get('Capabilities'),
            width: '220px',
            render: (item: ProviderConnection) => (
                <Group gap={4}>
                    <Text size={'sm'}>{capabilitySummary(item)}</Text>
                    {/* FNP-1233 — the declared write capability, shown only when
                        it is ON. An absent badge is the quiet default; a badge
                        reading "read-only" on every row would be noise on the
                        overwhelmingly common case. */}
                    {isWritableDeclared(item) && (
                        <Badge variant={'light'} color={'green'} size={'xs'}>
                            {I18n.get('writable')}
                        </Badge>
                    )}
                </Group>
            ),
        },
        {
            accessor: 'lastValidatedAt',
            sortable: true,
            title: I18n.get('Last validated'),
            width: '170px',
            render: (item: ProviderConnection) => (
                // ⚠️ NEVER VALIDATED AND VALIDATED-LONG-AGO ARE DIFFERENT
                // STATES. The shared formatter renders a placeholder for
                // nullish, but the explicit branch keeps "—" from being read as
                // a formatting failure.
                item.lastValidatedAt
                    ? <TableFormattedTimestamp record={item} propertyName={'lastValidatedAt'}/>
                    : <Text size={'sm'} c={'dimmed'}>{I18n.get('Never')}</Text>
            ),
        },
        // ⚠️ THERE IS DELIBERATELY NO CONNECTION ID COLUMN.
        //
        // `ProviderConnection.id` is opaque AND composite —
        // 'PROVIDER_CONNECTION#<accountId>#<uuid>'. Putting it in a table both
        // invites a tenant to treat it as something to paste around and puts
        // their account id on screen for no reason. The detail page addresses a
        // connection by it; nothing displays it.
        //
        // ⚠️ AND NO EXTERNAL ID COLUMN. It is per-connection, long, and needs
        // the explanation that only the detail view can give it. A column of
        // bare external ids invites copying without the trust-policy context
        // that makes them mean anything.
    ];

    return {
        columns,
    };
};
